import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../config';

export default function MessagesTechniques() {
    const [messages, setMessages] = useState([]);
    const [nonLus, setNonLus] = useState(0);
    const [loading, setLoading] = useState(true);
    const [openMessageId, setOpenMessageId] = useState(null);

    // Utilisation de la configuration centralisée
    const API_BASE = config.apiBase;

    const fetchMessages = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/get_messages_techniques.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setMessages(data.messages);
                setNonLus(data.non_lus);
            }
        } catch (error) {
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const marquerCommeLu = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_BASE}/marquer_message_lu.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id_message: id })
            });
            fetchMessages();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const supprimerMessage = async (id) => {
        if (!window.confirm('Supprimer ce message ?')) return;
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_BASE}/supprimer_message_technique.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id_message: id })
            });
            toast.success('Message supprimé');
            fetchMessages();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    useEffect(() => {
        fetchMessages();
        // Rafraîchir toutes les 30 secondes
        const interval = setInterval(fetchMessages, 30000);
        return () => clearInterval(interval);
    }, [fetchMessages]);

    const getTypeStyle = (type) => {
        switch(type) {
            case 'urgent': return 'bg-red-50 border-red-200 text-red-800';
            case 'warning': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
            case 'success': return 'bg-green-50 border-green-200 text-green-800';
            default: return 'bg-blue-50 border-blue-200 text-blue-800';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-4xl">
                {/* Header avec badge de notification */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white shadow-xl">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold">📬 Messages techniques</h1>
                            <p className="mt-2 opacity-90">
                                Communications officielles de l'équipe technique
                            </p>
                        </div>
                        {nonLus > 0 && (
                            <div className="relative">
                                <div className="flex h-16 w-16 animate-pulse items-center justify-center rounded-full bg-red-500">
                                    <span className="text-2xl font-bold">{nonLus}</span>
                                </div>
                                <div className="absolute -right-2 -top-2">
                                    <span className="flex h-3 w-3">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                                        <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500"></span>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Liste des messages */}
                {loading ? (
                    <div className="flex h-64 items-center justify-center">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600"></div>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="rounded-xl bg-white p-12 text-center shadow-lg">
                        <div className="text-5xl mb-4">📭</div>
                        <p className="text-slate-500">Aucun message technique</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {messages.map((msg) => (
                            <div
                                key={msg.id_message}
                                className={`rounded-xl border shadow-lg transition-all ${
                                    msg.lu ? 'bg-white' : 'bg-gradient-to-r from-white to-indigo-50 border-indigo-200'
                                } ${getTypeStyle(msg.type)}`}
                            >
                                <div
                                    className="cursor-pointer p-5"
                                    onClick={() => {
                                        if (!msg.lu) marquerCommeLu(msg.id_message);
                                        setOpenMessageId(openMessageId === msg.id_message ? null : msg.id_message);
                                    }}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="mb-2 flex items-center gap-3">
                                                <span className={`text-xl ${
                                                    msg.type === 'urgent' ? 'animate-pulse' : ''
                                                }`}>
                                                    {msg.type === 'urgent' ? '🔴' :
                                                     msg.type === 'warning' ? '⚠️' :
                                                     msg.type === 'success' ? '✅' : 'ℹ️'}
                                                </span>
                                                <span className="font-semibold">
                                                    {msg.type === 'urgent' ? 'URGENT' :
                                                     msg.type === 'warning' ? 'ATTENTION' :
                                                     msg.type === 'success' ? 'INFORMATION' : 'INFO'}
                                                </span>
                                                {!msg.lu && (
                                                    <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs text-white">
                                                        Nouveau
                                                    </span>
                                                )}
                                            </div>
                                            <p className={`line-clamp-2 ${!msg.lu ? 'font-semibold' : ''}`}>
                                                {msg.message}
                                            </p>
                                            <div className="mt-2 flex items-center gap-4 text-xs opacity-60">
                                                <span>📅 {new Date(msg.date_envoi).toLocaleString()}</span>
                                                <span>👤 Technique</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                supprimerMessage(msg.id_message);
                                            }}
                                            className="ml-4 rounded-lg p-2 text-slate-400 transition hover:bg-red-100 hover:text-red-600"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Message déplié */}
                                {openMessageId === msg.id_message && (
                                    <div className="border-t p-5" style={{ borderColor: 'inherit' }}>
                                        <p className="whitespace-pre-wrap text-sm">
                                            {msg.message}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}